<script setup>
import { computed, defineEmits } from 'vue';
import {
  calendarEventListItems,
  calendarEventRangeInfo,
  calendarEventScheduleLabel,
} from '@/js/core/utils/calendar-event-projections.js';
import {
  calendarEventDisplayTime,
  calendarEventMultiDayTime,
} from '@/js/core/utils/calendar-event-time.js';
import { EVENT_EXPIRY_PRECISION, isEventExpired } from '@/js/core/utils/event-expiry.js';
import CalendarPlusIcon from '@/js/core/components/icons/CalendarPlusIcon.vue';
import CalendarEventTime from '@/js/core/components/parts/calendar/CalendarEventTime.vue';
import {
  groupEventsByFilterGroup,
  hasAdvanceRegistration,
  hasLivestream,
  isCalendarCurrentMonth,
} from '@/js/core/utils/calendar-events.js';
import {
  calendarEventNavigationModeProp,
  useCalendarEventNavigation,
} from '@/js/core/composables/useCalendarEventNavigation.js';
import { DEFAULT_TIMEZONE } from '@/js/core/utils/timezone.js';
import { wrapBreakableTitle, plainTextFromHtml } from '@/js/core/utils/formatters.js';
import { eventAffiliationGroups } from '@/js/core/utils/event-affiliations.js';
import EventAffiliationLine from '@/js/core/components/parts/calendar/EventAffiliationLine.vue';

const props = defineProps({
  events: {
    type: Array,
    default: () => [],
  },
  activeRange: {
    type: Object,
    default: () => ({}),
  },
  timezone: {
    type: String,
    default: DEFAULT_TIMEZONE,
  },
  showPastEvents: {
    type: Boolean,
    default: false,
  },
  canTogglePastEvents: {
    type: Boolean,
    default: true,
  },
  hideExpiredEvents: {
    type: Boolean,
    default: false,
  },
  eventFilterGroups: {
    type: Object,
    default: null,
  },
  eventFilterFallbackGroup: {
    type: String,
    default: 'Other',
  },
  emptyMessage: {
    type: String,
    default: 'There are no results in this month.',
  },
  eventNavigationMode: calendarEventNavigationModeProp,
});

const emit = defineEmits(['toggle-past-events']);
const togglePastEvents = () => emit('toggle-past-events');
const isCurrentMonth = computed(() => isCalendarCurrentMonth(props.activeRange, props.timezone));
const { eventNavigation, handleEventClick, handleEventKeydown } = useCalendarEventNavigation(props);

const isPastEvent = event =>
  isEventExpired(event, {
    timezone: props.timezone,
    precision: props.hideExpiredEvents ? EVENT_EXPIRY_PRECISION.time : EVENT_EXPIRY_PRECISION.day,
  });

const groupedData = computed(() => {
  const filteredEvents = calendarEventListItems(props.events, {
    activeRange: props.activeRange,
    timezone: props.timezone,
    showPastEvents: props.showPastEvents,
    hideExpiredEvents: props.hideExpiredEvents,
  });

  return groupEventsByFilterGroup(
    filteredEvents,
    props.eventFilterGroups || undefined,
    props.eventFilterFallbackGroup
  );
});

const hasResults = computed(() => Object.keys(groupedData.value).length > 0);

const eventHasTime = event => Boolean(calendarEventDisplayTime(event, props.timezone));

const scheduleLabel = event => calendarEventScheduleLabel(event, props.timezone);

const multiDayTime = event =>
  calendarEventMultiDayTime(event, props.timezone, { expandCollapsedDates: true });

// Don't show affiliation in categories view for Academic Calendar entries
const showAffiliation = (groupTitle, event) =>
  groupTitle !== 'Academic Calendar' && eventAffiliationGroups(event).length > 0;

const slugify = text => {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .trim();
};
</script>

<template>
  <div class="calendar-view-categories">
    <div v-if="props.canTogglePastEvents && isCurrentMonth" class="view-past-event-toggle">
      <button type="button" class="view-past-event-toggle__link" @click="togglePastEvents">
        {{ props.showPastEvents ? 'hide past events' : 'show past events' }}
      </button>
    </div>

    <div v-if="!hasResults" class="no-results">{{ props.emptyMessage }}</div>

    <div
      v-for="(subCategories, groupTitle) in groupedData"
      :key="groupTitle"
      class="filter-group-section"
      :class="`${slugify(groupTitle)}`"
    >
      <h2 class="group-main-title">{{ groupTitle }}</h2>

      <div
        v-for="(groupEvents, subCatName) in subCategories"
        :key="subCatName"
        class="sub-category-block"
      >
        <h3 class="sub-cat-title">{{ subCatName }}</h3>

        <div class="category-grid">
          <div
            v-for="event in groupEvents"
            :key="event.id"
            class="category-event-card"
            :class="{ past: isPastEvent(event) }"
            :data-linkable="eventNavigation(event).isInteractive ? 'true' : undefined"
          >
            <component
              :is="eventNavigation(event).tag"
              class="event-link"
              :href="eventNavigation(event).href"
              :style="{
                cursor: eventNavigation(event).isInteractive ? 'pointer' : 'default',
              }"
              @click="handleEventClick($event, event)"
              @keydown="e => handleEventKeydown(e, event)"
            >
              <h4 class="event-title" v-html="wrapBreakableTitle(event.title)"></h4>
              <div v-if="(event.audience_labels || []).length" class="event-audiences">
                <div
                  v-for="audience in event.audience_labels"
                  :key="audience"
                  class="event-audience"
                >
                  {{ audience }}
                </div>
              </div>
              <div v-if="hasLivestream(event)" class="event-livestream">Livestream</div>
              <div v-if="hasAdvanceRegistration(event)" class="event-advance-registration">
                Advance Registration
              </div>
              <span v-if="scheduleLabel(event)" class="visually-hidden">
                {{ scheduleLabel(event) }}
              </span>
            </component>
            <div v-if="showAffiliation(groupTitle, event)" class="event-affiliation">
              <EventAffiliationLine :affiliations="eventAffiliationGroups(event)" />
            </div>
            <div v-if="event.location" class="event-location">{{ event.location }}</div>

            <div class="event-meta-top-first-row">
              <div class="occurrences-and-calendar">
                <div class="time-main-line">
                  <span
                    v-if="multiDayTime(event)"
                    class="multi-day-time"
                    v-html="multiDayTime(event)"
                  ></span>
                  <template v-else>
                    <span class="date-part">
                      {{ calendarEventRangeInfo(event, props.timezone).inlineDateLabel
                      }}<template v-if="eventHasTime(event)">,</template>
                    </span>
                    <CalendarEventTime
                      :event="event"
                      :timezone="props.timezone"
                      display="time"
                      class="time-part"
                    />

                    <span
                      v-if="calendarEventRangeInfo(event, props.timezone).isToday"
                      class="today-label-inline"
                      >Today</span
                    >
                  </template>
                </div>

                <div class="time-sub-line">
                  <CalendarEventTime
                    :event="event"
                    :timezone="props.timezone"
                    display="timezone"
                    class="timezone"
                  />

                  <a
                    v-if="event.ical_download_url"
                    :href="event.ical_download_url"
                    class="calendar-add-btn"
                    download
                    :aria-label="`Download calendar file for ${plainTextFromHtml(event.title)}`"
                  >
                    <CalendarPlusIcon class="calendar-icon" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
